import { db } from "../App";
import { normalizePresentationStatus } from "../interfaces/IPresentationStatus";
import { RoundLive, RoundLivePresentation } from "../interfaces/IRoundsLive";
import { doc, getDoc, onSnapshot } from "firebase/firestore";

type RoundPresentationRaw = {
  id?: string;
  status?: string;
  name?: string;
  title?: string;
  organizationName?: string;
  organization?: string;
  startNo?: unknown;
  startNumber?: unknown;
};

type RoundLiveDocument = {
  presentations?: RoundPresentationRaw[];
};

/** Port logiki host-panel: `startNo || startNumber || id` (+ normalizacja typów z Firestore). */
const resolvePresentationStartNo = (p: RoundPresentationRaw): number | string | undefined => {
  const tryCoerce = (value: unknown): number | string | undefined => {
    if (value === undefined || value === null || value === "") {
      return undefined;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string") {
      const t = value.trim();
      if (t === "") {
        return undefined;
      }
      const n = Number(t);
      if (Number.isFinite(n) && String(n) === t) {
        return n;
      }
      return t;
    }
    return undefined;
  };

  const fromFields = tryCoerce(p.startNo) ?? tryCoerce(p.startNumber);
  if (fromFields !== undefined) {
    return fromFields;
  }

  const id = p.id;
  if (typeof id === "string" && id.length > 0) {
    if (/^\d+$/.test(id)) {
      return parseInt(id, 10);
    }
    return id;
  }

  return undefined;
};

const roundDocumentRef = (competitionId: string, categoryId: string, roundId: string) =>
  doc(db, "competitions", competitionId, "categories", categoryId, "rounds", roundId);

const mapPresentation = (presentation: RoundPresentationRaw, index: number): RoundLivePresentation => ({
  id: presentation.id || `presentation-${index}`,
  status: normalizePresentationStatus(presentation.status),
  name: presentation.name,
  title: presentation.title,
  organizationName: presentation.organizationName ?? presentation.organization,
  startNo: resolvePresentationStartNo(presentation),
});

const mapRoundLive = (roundId: string, roundData?: RoundLiveDocument): RoundLive => ({
  id: roundId,
  presentations: (roundData?.presentations || []).map(mapPresentation),
});

export const findRoundLiveById = async (
  competitionId: string,
  categoryId: string,
  roundId: string,
): Promise<RoundLive | null> => {
  const snapshot = await getDoc(roundDocumentRef(competitionId, categoryId, roundId));
  if (!snapshot.exists()) {
    return null;
  }

  return mapRoundLive(roundId, snapshot.data() as RoundLiveDocument);
};

export const subscribeRoundLiveById = (
  competitionId: string,
  categoryId: string,
  roundId: string,
  onData: (round: RoundLive | null) => void,
  onError: (error: Error) => void,
) =>
  onSnapshot(
    roundDocumentRef(competitionId, categoryId, roundId),
    (snapshot) => {
      if (!snapshot.exists()) {
        onData(null);
        return;
      }

      onData(mapRoundLive(roundId, snapshot.data() as RoundLiveDocument));
    },
    (error) => {
      onError(error);
    },
  );
